import { atom, selector } from 'recoil'
import { authStateAtom } from '@/atoms/auth'
import { getAllNotificationsForCourse } from '@/api/cb/notification'
import {
    ContentCreatorNotification,
    ContentCreatorMnemonicSuggestionNotification,
} from 'common/src/api/contentFrontendVsContentBackend/types'

export const notificationsAtom = atom<ContentCreatorNotification[] | null>({
    key: 'notificationsAtom',
    default: selector<ContentCreatorNotification[]>({
        key: 'notificationsAtomFetcher',
        get: async ({ get }) => {
            if (!get(authStateAtom)) return null
            const authState = get(authStateAtom)
            return await fetchNotifications(
                authState.frontendDisplayedCourseName
            )
        },
    }),
})

export const numUnseenNotificationsSelector = selector<number>({
    key: 'numUnseenNotificationsSelector',
    get: ({ get }) => {
        return (
            get(notificationsAtom)?.filter(
                (notification) => !notification.hasBeenSeen
            )?.length ?? 0
        )
    },
})

export const notificationsTimestampAtom = atom<Date>({
    key: 'notificationsTimestampAtom',
    default: new Date(),
})

export const fetchNotifications = async (
    courseName: string
): Promise<ContentCreatorNotification[]> => {
    return (await getAllNotificationsForCourse(courseName)).data.payload
}

export const isNotificationsModalVisibleAtom = atom<boolean>({
    key: 'isNotificationsModalVisibleAtom',
    default: false,
})

export interface NotificationQueueData {
    notifications: (ContentCreatorNotification & { id: string })[]
    totalCount: number
}

export const notificationQueueAtom = atom<NotificationQueueData | null>({
    key: 'notificationQueueAtom',
    default: null,
})

export const notificationQueueFilterAtom = atom<
    'all' | 'incomplete' | 'complete'
>({
    key: 'notificationQueueFilterAtom',
    default: 'incomplete',
})

export const notificationQueueSortAtom = atom<'newest' | 'oldest'>({
    key: 'notificationQueueSortAtom',
    default: 'newest',
})

export const notificationQueuePageAtom = atom<number>({
    key: 'notificationQueuePageAtom',
    default: 1,
})

export const isNotificationQueueModalVisibleAtom = atom<boolean>({
    key: 'isNotificationQueueModalVisibleAtom',
    default: false,
})

export const selectedFeedbackNotificationAtom = atom<
    (ContentCreatorNotification & { id?: string }) | null
>({
    key: 'selectedFeedbackNotificationAtom',
    default: null,
})

// The mnemonic suggestion the creator clicked through to review. The
// notification deep-links to the notecard; the review panel inside the
// notecard editor picks the suggestion up from here and only renders when
// its notecardID matches the card being edited.
export const pendingMnemonicSuggestionAtom = atom<
    (ContentCreatorMnemonicSuggestionNotification & { id?: string }) | null
>({
    key: 'pendingMnemonicSuggestionAtom',
    default: null,
})
